import { useMemo, useCallback } from 'react';
import { ChatWidgetStatus, DeviceType } from '../../../../context/chat-widget-context/types';
import { isUrlAllowed } from '../../../../utils/urlUtils';
import { IFloatingButtonConfig, FloatingButtonActionType } from '../types';
import { useChatWidgetContext } from '../../../../context/chat-widget-context/chatWidgetContext';
import { useFalconContext } from '../../../../context/cms-context/FalconContext';

const useFloatingButtonUtils = () => {
  const { cmsContent } = useFalconContext();
  const { state } = useChatWidgetContext();

  /**
   * Main visibility condition for the floating buttons container.
   * Following requirements:
   * 1. Widget is allowed by URL.
   * 2. Chat is not still loading (waits for READY status).
   * 3. Agents are available (within business hours).
   * 4. Window open logic: hide if showFloatingButtonsOnWindowOpen is false and window is open.
   */
  const shouldShowFloatingButtons = useMemo(() => {
    const { showFloatingButtons, showFloatingButtonsOnWindowOpen } = cmsContent.chatWidget;

    const isWidgetAllowedByUrl = isUrlAllowed(
      cmsContent.chatWidget.allowedUrls || [],
      cmsContent.chatWidget.excludedUrls || [],
    );

    // Check global CMS toggle
    if (!showFloatingButtons) return false;

    // 1. Widget is not allowed by URL
    if (!isWidgetAllowedByUrl) return false;

    // 2. Chat is still loading (wait for onReady/READY status)
    if (state.status === ChatWidgetStatus.LOADING) return false;

    // 3. Agents are not available (business hours ended)
    if (!state.isAgentsAvailable) return false;

    // 4. Hide on mobile when window is open to avoid overlapping/clutter
    if (state.deviceType === DeviceType.MOBILE && state.isWindowOpen) {
      return false;
    }

    // 5. Check if there are any buttons configured
    if (cmsContent.floatingButtons.buttons.length === 0) {
      return false;
    }

    // 6. showFloatingButtonsOnWindowOpen prop logic:
    // If false, then buttons should not be visible when window is opened.
    if (!showFloatingButtonsOnWindowOpen && state.isWindowOpen) {
      return false;
    }

    // 7. User manually hide the buttons
    if (state.areFloatingButtonsHidden) {
      return false;
    }

    return true;
  }, [
    cmsContent.chatWidget.showFloatingButtons,
    cmsContent.chatWidget.showFloatingButtonsOnWindowOpen,
    state.status,
    state.isAgentsAvailable,
    state.isWindowOpen,
    state.deviceType,
    state.areFloatingButtonsHidden,
  ]);

  /**
   * Individual button visibility condition.
   * Checks for button-specific URL rules and state-dependent logic.
   */
  const memoizedButtonVisibilityCondition = useCallback(
    (config: IFloatingButtonConfig) => {
      // 1. Check if button is allowed by its specific URL configuration
      const isButtonAllowedByUrl = isUrlAllowed(
        config.allowedUrls || [],
        config.excludedUrls || [],
      );

      if (!isButtonAllowedByUrl) return false;

      // 2. Hide buttons if there is an error (they won't work)
      if (state.status === ChatWidgetStatus.ERROR) return false;

      // 3. Check legal acceptance for chat-related actions
      if (config.type === FloatingButtonActionType.CHAT_MESSAGE && !state.isLegalAccepted) {
        return false;
      }

      return true;
    },
    [state.status, state.isLegalAccepted],
  );

  return {
    shouldShowFloatingButtons,
    memoizedButtonVisibilityCondition,
  };
};

export default useFloatingButtonUtils;
